import React, { useEffect, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CircularProgress, TextField } from '@material-ui/core';
import styled from 'styled-components/macro';
import { withStyles } from '@material-ui/core/styles';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/dropdown-arrow.svg';
import { Typography } from '@deeptrue-frontend/ui';
import { useDebounce } from 'hooks/useDebouce';

const StyledLabel = styled.label<{ hasError?: boolean }>`
  display: inline-block;
  margin-bottom: 1rem;
  width: 100%;
  cursor: pointer;
  color: #42526e;
  font-weight: 500;
  p {
    margin-bottom: 0.5rem;
  }
  .MuiOutlinedInput-root {
    &:hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: #d6d6d6;
      }
    }
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ hasError }) =>
      hasError ? '#DC3545 !important' : '#d6d6d6'};
  }
`;

const GlobalCss = withStyles({
  '@global': {
    '.MuiAutocomplete-endAdornment': {
      paddingRight: '9px',
    },
  },
})(() => null);

export type OptionType = {
  value: number | string;
  label: string;
};

interface Props {
  options: OptionType[];
  name: string;
  label?: string | React.ReactElement;
  helperText?: React.ReactNode;
  hasError?: boolean;
  multiple?: boolean;
  asyncSeach?: (searchPhrase: string) => Promise<any>;
  onChange: (field: string, value: any) => void;
  value?: OptionType[];
  placeholder?: string;
  styles?: any;
}

const Search = ({
  options,
  label,
  helperText,
  hasError,
  multiple,
  asyncSeach,
  onChange,
  name,
  value,
  placeholder,
  styles,
}: Props) => {
  const [searchedValue, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const debouncedValue = useDebounce(searchedValue);

  useEffect(() => {
    if (asyncSeach) {
      asyncSeach(debouncedValue).finally(() => {
        setIsSearching(false);
      });
    }
    // eslint-disable-next-line
  }, [debouncedValue]);

  return (
    <>
      <StyledLabel hasError={hasError}>
        {label && (
          <Typography
            variant="copy1"
            display="block"
            mb="1rem"
            color={hasError ? 'punch' : undefined}
          >
            {label}
          </Typography>
        )}
        <GlobalCss />
        <Autocomplete
          popupIcon={<CheckmarkIcon width={16} height={16} />}
          onInputChange={(_e, value, reason) => {
            if (reason === 'reset') {
              setSearch('');
            } else {
              setIsSearching(true);
              setSearch(value);
            }
          }}
          getOptionSelected={(
            option: OptionType,
            selectedValue: OptionType
          ) => {
            return option.value === selectedValue.value;
          }}
          value={value}
          onChange={(_e, value) => {
            onChange(name, value);
            setIsSearching(false);
          }}
          style={{
            backgroundColor: 'white',
            ...styles,
          }}
          getOptionLabel={(option: OptionType) => option.label}
          options={options}
          inputValue={searchedValue}
          multiple={multiple}
          loading={isSearching}
          placeholder={placeholder}
          renderInput={(params: any) => {
            return (
              <TextField
                {...params}
                variant="outlined"
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isSearching ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
        />
        {helperText && (
          <Typography
            variant="copy1"
            display="block"
            mt="0.75rem"
            color={hasError ? '#DC3545 !important' : 'driarGray'}
            fontWeight={'400 !important'}
          >
            {helperText}
          </Typography>
        )}
      </StyledLabel>
    </>
  );
};

export default Search;
